import React, { useState, useEffect, useCallback } from 'react';
import { Button, Form, Alert, Spinner, Card, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagic, faDownload, faCheck, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import './AIImageGenerator.css';
import api from '../config/api';

// Maksimum görsel oluşturma limiti
const MAX_AI_IMAGE_LIMIT = 5;

const AIImageGenerator = ({ onImageSelect }) => {
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [error, setError] = useState(null);
  const [remainingPrompts, setRemainingPrompts] = useState(MAX_AI_IMAGE_LIMIT);
  const [isSuccess, setIsSuccess] = useState(false);
  const { currentUser, token } = useAuth();
  
  // Bugünün tarihini YYYY-MM-DD formatında döndüren yardımcı fonksiyon
  const getTodayDateString = () => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  };
  
  // Kullanıcı ID'sini LocalStorage için getiren fonksiyon
  const getUserId = () => {
    if (currentUser && currentUser.id) return currentUser.id;
    // Kullanıcı giriş yapmamışsa, tarayıcı fingerprint'i oluştur
    let anonymousId = localStorage.getItem('anonymousUserId');
    if (!anonymousId) {
      anonymousId = 'anonymous_' + Math.random().toString(36).substring(2, 15);
      localStorage.setItem('anonymousUserId', anonymousId);
    }
    return anonymousId;
  };
  
  // Kullanım sayacını sıfırla
  const resetUsageCount = () => {
    const userId = getUserId();
    const todayStr = getTodayDateString();
    const storageKey = `ai_usage_${userId}`;
    
    const newUsageData = {
      date: todayStr,
      count: 0
    };
    
    localStorage.setItem(storageKey, JSON.stringify(newUsageData));
    setRemainingPrompts(MAX_AI_IMAGE_LIMIT);
  };
  
  // Kullanım sayacını artır
  const incrementUsageCount = () => {
    const userId = getUserId();
    const todayStr = getTodayDateString();
    const storageKey = `ai_usage_${userId}`;
    
    // Mevcut kullanım verisini al
    let usageData = localStorage.getItem(storageKey);
    
    if (usageData) {
      try {
        usageData = JSON.parse(usageData);
        
        // Eğer veri bugüne ait değilse, sıfırla
        if (usageData.date !== todayStr) {
          usageData = {
            date: todayStr,
            count: 0
          };
        }
      } catch (err) {
        console.error('LocalStorage verisi ayrıştırılırken hata:', err);
        usageData = {
          date: todayStr,
          count: 0
        };
      }
    } else {
      usageData = {
        date: todayStr,
        count: 0
      };
    }
    
    // Kullanım sayısını artır
    usageData.count += 1;
    
    // LocalStorage'a geri kaydet
    localStorage.setItem(storageKey, JSON.stringify(usageData));
    
    // Kalan hak sayısını güncelle
    const remainingCount = Math.max(0, MAX_AI_IMAGE_LIMIT - usageData.count);
    setRemainingPrompts(remainingCount);
    
    return remainingCount;
  };
  
  // LocalStorage'dan kullanım sayısını al
  useEffect(() => {
    const userId = getUserId();
    const todayStr = getTodayDateString();
    const storageKey = `ai_usage_${userId}`;
    
    // LocalStorage'dan kullanım verisini al
    let usageData = localStorage.getItem(storageKey);
    
    if (usageData) {
      try {
        usageData = JSON.parse(usageData);
        
        // Eğer veri bugüne ait değilse, sıfırla
        if (usageData.date !== todayStr) {
          const newUsageData = {
            date: todayStr,
            count: 0
          };
          localStorage.setItem(storageKey, JSON.stringify(newUsageData));
          setRemainingPrompts(MAX_AI_IMAGE_LIMIT);
        } else {
          // Bugüne ait veri varsa, kalan hakkı hesapla
          setRemainingPrompts(Math.max(0, MAX_AI_IMAGE_LIMIT - usageData.count));
        }
      } catch (err) {
        console.error('LocalStorage verisi ayrıştırılırken hata:', err);
        resetUsageCount();
      }
    } else {
      // Hiç veri yoksa, yeni oluştur
      resetUsageCount();
    }
  }, [currentUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!prompt.trim()) {
      setError('Lütfen bir açıklama girin.');
      return;
    }

    if (remainingPrompts <= 0) {
      setError(`Günlük maksimum ${MAX_AI_IMAGE_LIMIT} görsel oluşturma sınırına ulaştınız. Yarın tekrar deneyin.`);
      return;
    }

    setError(null);
    setIsGenerating(true);
    setGeneratedImage(null);
    
    try {
      // API isteği yapmak yerine, LocalStorage üzerinden kontrol
      // Gerçek API istek kodunu burada tutuyoruz
      const response = await api.post('/ai-images/generate', { 
        prompt: prompt.trim() 
      });
      
      setGeneratedImage(response.data.imageUrl);
      const remaining = incrementUsageCount();
      setIsSuccess(true);
      
      setTimeout(() => {
        setIsSuccess(false);
      }, 3000);
      
    } catch (err) {
      console.error('Görsel oluşturma hatası:', err);
      
      if (err.response?.status === 429) {
        setError('Çok fazla istek gönderildi. Lütfen biraz bekleyip tekrar deneyin.');
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Görsel oluşturulurken bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUseImage = () => {
    if (generatedImage && onImageSelect) {
      onImageSelect(generatedImage);
    }
  };

  return (
    <div className="ai-image-generator">
      <div className="ai-header">
        <h4>
          <FontAwesomeIcon icon={faMagic} className="me-2" />
          Yapay Zeka ile Görsel Oluştur
        </h4>
      </div>
      
      <p className="ai-description">
        Ne tür bir görsel oluşturmak istediğinizi detaylı olarak açıklayın.
        Oluşturulan görseller tasarımınıza eklenebilir.
      </p>
      
      {/* Kalan hak bilgisi */}
      <div className="remaining-credits alert alert-info mb-3">
        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
        Günlük kalan görsel oluşturma hakkınız: <strong>{remainingPrompts}</strong> / {MAX_AI_IMAGE_LIMIT}
      </div>
      
      {error && (
        <Alert variant="danger" className="mt-3">
          {error}
        </Alert>
      )}
      
      {isSuccess && (
        <Alert variant="success" className="mt-3">
          <FontAwesomeIcon icon={faCheck} className="me-2" />
          Görsel başarıyla oluşturuldu!
        </Alert>
      )}
      
      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Control
            as="textarea"
            rows={3}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Örn: Bir masada duran ahşap bir kahve bardağı, sabah ışığında, minimalist tarzda"
            disabled={isGenerating || remainingPrompts <= 0}
          />
        </Form.Group>
        
        <div className="d-grid gap-2">
          <Button 
            type="submit" 
            variant="primary" 
            disabled={isGenerating || !prompt.trim() || remainingPrompts <= 0}
            className="generate-button"
          >
            {isGenerating ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                <span className="ms-2">Oluşturuluyor...</span>
              </>
            ) : remainingPrompts <= 0 ? (
              <>Hakkınız Kalmadı</>
            ) : (
              <>Görsel Oluştur</>
            )}
          </Button>
        </div>
      </Form>
      
      {generatedImage && (
        <div className="generated-image-container mt-4">
          <img 
            src={generatedImage} 
            alt="Yapay zeka tarafından oluşturulan görsel" 
            className="generated-image"
          />
          
          <div className="image-actions mt-3">
            <Button 
              variant="success" 
              onClick={handleUseImage}
              className="w-100"
            >
              Tasarımda Kullan
            </Button>
            
            <Button 
              variant="outline-secondary" 
              href={generatedImage} 
              download="ai-generated-image.png"
              target="_blank"
              rel="noopener noreferrer"
              className="w-100 mt-2"
            >
              <FontAwesomeIcon icon={faDownload} className="me-2" />
              İndir
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIImageGenerator; 